"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDialogFocus } from "./use-dialog-focus";
import { searchKnowledge, type KnowledgeRecord } from "@/lib/knowledge";

type GlobalSearchProps = {
  open: boolean;
  records: KnowledgeRecord[];
  onClose: () => void;
  onNavigate: (record: KnowledgeRecord) => void;
};

const kindLabels: Record<KnowledgeRecord["kind"], string> = {
  source: "Source",
  signal: "Signal",
  claim: "Claim",
  system: "System",
  simulation: "Simulation",
  projection: "Projected",
  timeline: "Timeline",
  portfolio: "Portfolio",
  media: "Media",
};

export default function GlobalSearch({
  open,
  records,
  onClose,
  onNavigate,
}: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const panelRef = useRef<HTMLElement>(null);
  useDialogFocus(open, panelRef, onClose);
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(
    () => searchKnowledge(records, query, { limit: 16 }).results,
    [query, records],
  );

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="command-backdrop">
      <button
        type="button"
        className="backdrop-dismiss"
        aria-label="Close search"
        onClick={onClose}
      />
      <section ref={panelRef}
        aria-label="Search Asymmetric"
        aria-modal="true"
        className="command-panel"
        role="dialog"
      >
        <header className="command-input-row">
          <span aria-hidden="true">/</span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setSelectedIndex(0); }}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={Boolean(results.length)}
            aria-controls="global-search-results"
            aria-activedescendant={results[selectedIndex] ? `search-result-${selectedIndex}` : undefined}
            onKeyDown={event => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const next = results.length ? (selectedIndex + (event.key === "ArrowDown" ? 1 : -1) + results.length) % results.length : 0;
                setSelectedIndex(next);
                document.getElementById(`search-result-${next}`)?.scrollIntoView({ block: "nearest" });
              } else if (event.key === "Enter" && results[selectedIndex]) {
                event.preventDefault(); onNavigate(results[selectedIndex]);
              }
            }}
            placeholder="Search Asymmetric…"
            aria-label="Search query"
          />
          <button type="button" onClick={onClose}>Esc</button>
        </header>

        <div className="command-results" id="global-search-results" role="listbox" aria-label="Matching records" aria-live="polite">
          {results.map((record, index) => (
            <button
              type="button"
              className="command-result"
              role="option"
              id={`search-result-${index}`}
              aria-selected={selectedIndex === index}
              onMouseEnter={() => setSelectedIndex(index)}
              key={record.id}
              onClick={() => onNavigate(record)}
            >
              <span>{kindLabels[record.kind]}</span>
              <strong>{record.title}</strong>
              <small>{record.subtitle}</small>
            </button>
          ))}
          {!results.length && (
            <p>No matching records. Try another search.</p>
          )}
        </div>

        <footer>
          <span>{records.length} indexed records</span>
          <span>Read only</span>
        </footer>
      </section>
    </div>
  );
}
